/**
 * Widget-bro (JS-side).
 *
 * Kommuniserer med den native `WidgetBridge`-pluginen (egen plugin, IKKE FricampCar
 * — widget-data har annen livssyklus: persisteres til disk og leses av en EGEN
 * prosess/extension, ikke in-memory samme prosess). Pluginen skriver snapshotet til
 * delt lagring (iOS App Group / Android SharedPreferences) og ber widgeten
 * re-rendre.
 *
 * S1 (denne PR-en) leverer JS-grensesnittet + throttle-gaten. Native-implementasjonen
 * (Swift/Kotlin) + widget-targets kommer i S2/S3. På web (og før native-pluginen
 * finnes) er alt her en trygg no-op.
 *
 *   buildWidgetSnapshot() ─► pushWidgetSnapshot() ─[shouldWriteSnapshot?]─► WidgetBridge.updateSnapshot(JSON)
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { shouldWriteSnapshot, type WidgetSnapshot } from './widgetSnapshotService';

interface WidgetBridgePlugin {
  /** Skriv snapshotet (JSON-streng) til delt lagring og trig widget-reload. */
  updateSnapshot(options: { snapshot: string }): Promise<void>;
  /** Tøm delt lagring (f.eks. ved utlogging). */
  clear(): Promise<void>;
}

const WidgetBridge: WidgetBridgePlugin | null = Capacitor.isNativePlatform()
  ? registerPlugin<WidgetBridgePlugin>('WidgetBridge')
  : null;

// Throttle-hukommelse: siste snapshot vi faktisk skrev.
let lastWritten: WidgetSnapshot | null = null;

/**
 * Skriv snapshotet til widgeten hvis noe synlig har endret seg (throttle via
 * `shouldWriteSnapshot`). No-op på web / uten native-plugin. Feil svelges — en
 * mislykket widget-oppdatering skal aldri påvirke appen.
 */
export async function pushWidgetSnapshot(snapshot: WidgetSnapshot): Promise<void> {
  if (!WidgetBridge) return;
  if (!shouldWriteSnapshot(lastWritten, snapshot)) return;
  lastWritten = snapshot;
  try {
    await WidgetBridge.updateSnapshot({ snapshot: JSON.stringify(snapshot) });
  } catch (e) {
    console.warn('[WidgetBridge] updateSnapshot feilet:', e);
  }
}

/** Tøm widget-data (utlogging). No-op på web. */
export async function clearWidget(): Promise<void> {
  if (!WidgetBridge) return;
  lastWritten = null;
  try {
    await WidgetBridge.clear();
  } catch (e) {
    console.warn('[WidgetBridge] clear feilet:', e);
  }
}

/** Test-hook: nullstill throttle-hukommelsen. */
export function __resetWidgetBridgeForTest(): void {
  lastWritten = null;
}
